(() => {
  if (window.CiariPdpVisualReadyV4) return;
  window.CiariPdpVisualReadyV4 = true;

  class CiariPdpVideoModal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.section = this.closest('.ciari-pdp-main');
      this.dialog = this.querySelector('.ciari-pdp-video-modal');
      this.stage = this.querySelector('[data-ciari-pdp-video-stage]');
      this.template = this.querySelector('[data-ciari-pdp-video-player]');
      if (!this.section || !this.dialog || !this.stage || !this.template) return;

      this.toneOpeners = [...this.section.querySelectorAll('[data-ciari-pdp-video-source="tone-player"]')];
      if (!this.getToneVideo()) {
        this.toneOpeners.forEach((opener) => {
          if ('disabled' in opener) opener.disabled = true;
          opener.setAttribute('aria-disabled', 'true');
        });
      }

      this.onSectionClick = (event) => {
        const opener = event.target.closest('[data-ciari-pdp-video-open]');
        if (!opener || !this.section.contains(opener)) return;
        this.open(opener);
      };
      this.onDialogClick = (event) => {
        if (event.target === this.dialog || event.target.closest('[data-ciari-pdp-video-close]')) this.close();
      };
      this.onCancel = (event) => {
        event.preventDefault();
        this.close();
      };
      this.onClosed = () => {
        this.teardownPlayer();
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
        this.opener = null;
      };

      this.section.addEventListener('click', this.onSectionClick);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClosed);
    }

    disconnectedCallback() {
      this.section?.removeEventListener('click', this.onSectionClick);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('cancel', this.onCancel);
      this.dialog?.removeEventListener('close', this.onClosed);
      this.teardownPlayer();
      this.initialized = false;
    }

    open(opener) {
      if (this.dialog.open) return;
      const useTonePlayer = opener.dataset.ciariPdpVideoSource === 'tone-player';
      const toneVideo = useTonePlayer ? this.getToneVideo() : null;
      const player = useTonePlayer ? toneVideo?.cloneNode(true) : this.template.content.cloneNode(true);
      if (!player) return;
      if (!player.childNodes?.length && !player.matches?.('video')) return;
      this.opener = opener;
      this.stage.replaceChildren(player);
      this.dialog.showModal();
      const video = this.stage.querySelector('video');
      if (video) {
        const played = video.play();
        if (played?.catch) played.catch(() => {});
      }
    }

    getToneVideo() {
      return document.querySelector('[data-ciari-tone-player] [data-ciari-tone-slide]:not([hidden]) video');
    }

    close() {
      if (!this.dialog.open) return;
      this.teardownPlayer();
      this.dialog.close();
    }

    teardownPlayer() {
      this.stage?.replaceChildren();
    }
  }

  if (!customElements.get('ciari-pdp-video-modal')) {
    customElements.define('ciari-pdp-video-modal', CiariPdpVideoModal);
  }

  class CiariTonePlayer extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-ciari-tone-slide]')];
      this.playButton = this.querySelector('[data-ciari-tone-play]');
      this.closeButton = this.querySelector('[data-ciari-tone-close]');
      this.videos = this.slides.map((slide) => slide.querySelector('video')).filter(Boolean);
      this.currentIndex = Number(this.dataset.toneIndex || 0);
      if (!this.slides.length) return;

      this.onClick = (event) => {
        if (event.target.closest('[data-ciari-tone-play]')) {
          this.playCurrent();
          return;
        }
        if (event.target.closest('[data-ciari-tone-close]')) {
          this.restorePlayback(this.activeVideo, true);
          return;
        }
        if (event.target.closest('[data-ciari-tone-prev]')) this.show(this.currentIndex - 1);
        if (event.target.closest('[data-ciari-tone-next]')) this.show(this.currentIndex + 1);
      };
      this.addEventListener('click', this.onClick);

      this.onVideoEnded = (event) => this.restorePlayback(event.currentTarget);
      this.onWebkitBeginFullscreen = (event) => {
        this.fullscreenVideo = event.currentTarget;
        this.activeVideo = event.currentTarget;
      };
      this.onWebkitEndFullscreen = (event) => this.restorePlayback(event.currentTarget, true);
      this.videos.forEach((video) => {
        video.addEventListener('ended', this.onVideoEnded);
        video.addEventListener('webkitbeginfullscreen', this.onWebkitBeginFullscreen);
        video.addEventListener('webkitendfullscreen', this.onWebkitEndFullscreen);
      });

      this.onFullscreenChange = () => {
        const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
        if (fullscreenElement && this.contains(fullscreenElement)) {
          this.fullscreenVideo = fullscreenElement.matches('video')
            ? fullscreenElement
            : fullscreenElement.querySelector('video');
          return;
        }
        if (this.fullscreenVideo) this.restorePlayback(this.fullscreenVideo, true);
      };
      this.onKeydown = (event) => {
        const video = this.fullscreenVideo || this.activeVideo;
        if (event.key !== 'Escape' || !video) return;
        const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
        if (fullscreenElement && this.contains(fullscreenElement) && document.exitFullscreen) {
          event.preventDefault();
          document.exitFullscreen().catch(() => this.restorePlayback(video, true));
          return;
        }
        if (video.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
          event.preventDefault();
          video.webkitExitFullscreen();
          return;
        }
        event.preventDefault();
        this.restorePlayback(video, true);
      };
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
      document.addEventListener('keydown', this.onKeydown);

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting && this.activeVideo) this.restorePlayback(this.activeVideo);
      }, { threshold: 0.05 });
      this.visibilityObserver.observe(this);
      this.show(this.currentIndex);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.videos?.forEach((video) => {
        video.removeEventListener('ended', this.onVideoEnded);
        video.removeEventListener('webkitbeginfullscreen', this.onWebkitBeginFullscreen);
        video.removeEventListener('webkitendfullscreen', this.onWebkitEndFullscreen);
      });
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
      document.removeEventListener('keydown', this.onKeydown);
      this.visibilityObserver?.disconnect();
      this.pauseAll();
    }

    show(index) {
      if (!this.slides.length) return;
      this.pauseAll();
      this.currentIndex = (index + this.slides.length) % this.slides.length;
      this.dataset.toneIndex = String(this.currentIndex);
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      this.resetPoster();
    }

    playCurrent() {
      const video = this.slides[this.currentIndex]?.querySelector('video');
      if (!video) return;
      this.activeVideo = video;
      this.classList.add('is-playing');
      if (this.closeButton) this.closeButton.hidden = false;
      video.preload = 'auto';
      video.play().catch(() => this.restorePlayback(video));
    }

    pauseAll() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    resetPoster() {
      this.classList.remove('is-playing');
      if (this.closeButton) this.closeButton.hidden = true;
    }

    restorePlayback(video, restoreFocus = false) {
      video?.pause();
      if (video) video.currentTime = 0;
      this.fullscreenVideo = null;
      this.activeVideo = null;
      this.resetPoster();
      if (restoreFocus) this.playButton?.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('ciari-tone-player')) {
    customElements.define('ciari-tone-player', CiariTonePlayer);
  }

  class CiariFeatureRing extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.items = [...this.querySelectorAll('.cpde__callout')]
        .sort((first, second) => Number(first.dataset.featureOrder) - Number(second.dataset.featureOrder));
      this.buttons = this.items.map((item) => item.querySelector('[data-ciari-feature-hotspot]')).filter(Boolean);
      if (!this.buttons.length) return;

      this.mobileQuery = matchMedia('(max-width: 989px)');
      this.reduceMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = Math.max(0, this.buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true'));
      this.isVisible = !('IntersectionObserver' in window);

      this.onClick = (event) => {
        const button = event.target.closest('[data-ciari-feature-hotspot]');
        if (!button || !this.contains(button)) return;
        this.select(this.buttons.indexOf(button));
      };
      this.onFocusIn = (event) => {
        try {
          if (!event.target.matches(':focus-visible')) return;
        } catch (_error) {
          return;
        }
        this.keyboardPaused = true;
        this.clearTimer();
      };
      this.onFocusOut = () => {
        this.keyboardPaused = false;
        this.schedule();
      };
      this.onEnvironmentChange = () => this.schedule();
      this.onVisibilityChange = () => this.schedule();

      this.addEventListener('click', this.onClick);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.mobileQuery.addEventListener('change', this.onEnvironmentChange);
      this.reduceMotionQuery.addEventListener('change', this.onEnvironmentChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.schedule();
        }, { threshold: 0.1 });
        this.visibilityObserver.observe(this);
      }

      this.select(this.currentIndex);
    }

    disconnectedCallback() {
      this.clearTimer();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.mobileQuery?.removeEventListener('change', this.onEnvironmentChange);
      this.reduceMotionQuery?.removeEventListener('change', this.onEnvironmentChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.visibilityObserver?.disconnect();
      this.initialized = false;
    }

    select(index) {
      if (index < 0 || index >= this.items.length) return;
      this.currentIndex = index;
      this.items.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        this.buttons[itemIndex]?.setAttribute('aria-pressed', String(active));
      });
      this.schedule();
    }

    canRotate() {
      return this.dataset.autoplay === 'true'
        && this.buttons.length > 1
        && Boolean(this.closest('.cpde--core-pro'))
        && this.mobileQuery.matches
        && !this.reduceMotionQuery.matches
        && !this.keyboardPaused
        && !document.hidden
        && this.isVisible;
    }

    schedule() {
      this.clearTimer();
      if (!this.canRotate()) return;
      const delay = Number.parseInt(this.dataset.autoplayDelay, 10) || 4_000;
      this.rotationTimer = window.setTimeout(() => {
        this.select((this.currentIndex + 1) % this.items.length);
      }, delay);
    }

    clearTimer() {
      window.clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  if (!customElements.get('ciari-feature-ring')) {
    customElements.define('ciari-feature-ring', CiariFeatureRing);
  }

  class CiariEditorialTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.buttons = [...this.querySelectorAll('[data-ciari-editorial-state]')];
      this.panels = [...this.querySelectorAll('[data-ciari-editorial-media-panel]')];
      if (!this.buttons.length) return;

      this.reduceMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = Math.max(0, this.buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true'));
      this.isVisible = !('IntersectionObserver' in window);

      this.onClick = (event) => {
        const button = event.target.closest('[data-ciari-editorial-state]');
        if (!button || !this.contains(button)) return;
        this.select(this.buttons.indexOf(button));
      };
      this.onKeydown = (event) => {
        const button = event.target.closest('[data-ciari-editorial-state]');
        if (!button || !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const currentIndex = this.buttons.indexOf(button);
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = this.buttons.length - 1;
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
        else nextIndex = (currentIndex + 1) % this.buttons.length;
        this.buttons[nextIndex].focus();
        this.select(nextIndex);
      };
      this.onPointerOver = (event) => {
        if (event.pointerType !== 'mouse') return;

        const button = event.target.closest('[data-ciari-editorial-state]');
        if (!button || !this.contains(button) || button.contains(event.relatedTarget)) return;

        this.pointerPaused = true;
        this.clearTimer();
        this.select(this.buttons.indexOf(button));
      };
      this.onPointerLeave = (event) => {
        if (event.pointerType !== 'mouse') return;
        this.pointerPaused = false;
        this.schedule();
      };
      this.onFocusIn = (event) => {
        const button = event.target.closest('[data-ciari-editorial-state]');
        if (button && this.contains(button)) this.select(this.buttons.indexOf(button));

        try {
          if (!event.target.matches(':focus-visible')) return;
        } catch (_error) {
          return;
        }
        this.keyboardPaused = true;
        this.clearTimer();
      };
      this.onFocusOut = () => {
        this.keyboardPaused = false;
        this.schedule();
      };
      this.onEnvironmentChange = () => this.schedule();
      this.onVisibilityChange = () => this.schedule();

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.reduceMotionQuery.addEventListener('change', this.onEnvironmentChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.schedule();
        }, { threshold: 0.1 });
        this.visibilityObserver.observe(this);
      }

      this.select(this.currentIndex);
    }

    disconnectedCallback() {
      this.clearTimer();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.reduceMotionQuery?.removeEventListener('change', this.onEnvironmentChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.visibilityObserver?.disconnect();
      this.initialized = false;
    }

    select(index) {
      if (index < 0 || index >= this.buttons.length) return;
      this.currentIndex = index;
      const state = this.buttons[index].dataset.ciariEditorialState;
      this.buttons.forEach((button, buttonIndex) => {
        const selected = buttonIndex === index;
        button.setAttribute('aria-pressed', String(selected));
        button.closest('li')?.classList.toggle('is-active', selected);
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.ciariEditorialMediaPanel !== state;
      });
      this.schedule();
    }

    canRotate() {
      return this.dataset.autoplay === 'true'
        && this.buttons.length > 1
        && !this.reduceMotionQuery.matches
        && !this.pointerPaused
        && !this.keyboardPaused
        && !document.hidden
        && this.isVisible;
    }

    schedule() {
      this.clearTimer();
      if (!this.canRotate()) return;
      const delay = Number.parseInt(this.dataset.autoplayDelay, 10) || 4_000;
      this.rotationTimer = window.setTimeout(() => {
        this.select((this.currentIndex + 1) % this.buttons.length);
      }, delay);
    }

    clearTimer() {
      window.clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  if (!customElements.get('ciari-editorial-tabs')) {
    customElements.define('ciari-editorial-tabs', CiariEditorialTabs);
  }

  class CiariSteveCtaSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.modelSelect = this.querySelector('[data-ciari-steve-cta-model]');
      this.variantSelect = this.querySelector('[data-ciari-steve-cta-variant]');
      this.submitButton = this.querySelector('[data-ciari-steve-cta-submit]');
      this.buttonLabel = this.querySelector('[data-ciari-steve-cta-label]');
      this.loadingLabel = this.querySelector('[data-ciari-steve-cta-loading]');
      this.status = this.querySelector('[data-ciari-steve-cta-status]');
      if (!this.modelSelect || !this.variantSelect || !this.submitButton) return;

      this.onModelChange = () => this.updateVariants();
      this.onSubmit = () => this.addSelectedVariant();
      this.modelSelect.addEventListener('change', this.onModelChange);
      this.submitButton.addEventListener('click', this.onSubmit);
      this.updateVariants();
    }

    disconnectedCallback() {
      this.modelSelect?.removeEventListener('change', this.onModelChange);
      this.submitButton?.removeEventListener('click', this.onSubmit);
      this.initialized = false;
    }

    updateVariants() {
      const selectedHandle = this.modelSelect.value;
      const optionsTemplate = [...this.querySelectorAll('[data-ciari-steve-cta-options]')]
        .find((template) => template.dataset.ciariSteveCtaOptions === selectedHandle);
      this.variantSelect.replaceChildren(optionsTemplate?.content.cloneNode(true) || document.createDocumentFragment());
      const firstAvailableOption = [...this.variantSelect.options].find((option) => !option.disabled);
      if (firstAvailableOption) this.variantSelect.value = firstAvailableOption.value;
      this.submitButton.disabled = !firstAvailableOption;
      this.setStatus(firstAvailableOption ? '' : 'This model is currently sold out.');
    }

    async addSelectedVariant() {
      const selectedOption = this.variantSelect.selectedOptions[0];
      if (!selectedOption || selectedOption.disabled || this.submitButton.disabled) return;

      const pageContainer = document.getElementById('PageContainer');
      const cartType = pageContainer?.dataset.cartType;
      const cartAction = pageContainer?.dataset.cartAction;
      const payload = { items: [{ id: Number(selectedOption.value), quantity: 1 }] };
      if (cartType === 'drawer') payload.sections = 'cart-drawer';

      this.setLoading(true);
      this.setStatus('');

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const responseJson = await response.json();
        if (!response.ok) throw new Error(responseJson.description || 'Unable to add this guitar to the cart.');

        if (cartType === 'drawer') window.eventBus?.emit('update:cart:drawer', responseJson);
        window.eventBus?.emit('cart:added', { sectionId: this.dataset.sectionId });

        const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}?view=compare`);
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          window.wetheme?.updateCartCount(cart);
        }

        this.setStatus('Added to cart.');
        if (cartType === 'drawer' && cartAction === 'go_to_or_open_cart') {
          window.eventBus?.emit('open:cart:drawer', { scrollToTop: true });
        } else if (cartAction !== 'show_added_message') {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        this.setStatus(error.message || 'Unable to add this guitar to the cart.', true);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (this.buttonLabel) this.buttonLabel.hidden = loading;
      if (this.loadingLabel) this.loadingLabel.hidden = !loading;
      this.submitButton.disabled = loading || !this.variantSelect.selectedOptions[0] || this.variantSelect.selectedOptions[0].disabled;
      this.modelSelect.disabled = loading;
      this.variantSelect.disabled = loading;
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', isError);
    }
  }

  if (!customElements.get('ciari-steve-cta-selector')) {
    customElements.define('ciari-steve-cta-selector', CiariSteveCtaSelector);
  }

  const syncCoreStoryOrder = () => {
    if (!document.querySelector('.ciari-pdp-main--core')) return;
    const toneSection = document.querySelector('.cpde--core-tone')?.closest('.shopify-section');
    const sliderSection = document
      .querySelector('[data-wetheme-section-type="ciari--product-slider"]')
      ?.closest('.shopify-section');
    if (!toneSection || !sliderSection || toneSection.parentElement !== sliderSection.parentElement) return;

    if (matchMedia('(min-width: 990px)').matches) {
      if (toneSection.nextElementSibling === sliderSection) {
        toneSection.parentElement.insertBefore(sliderSection, toneSection);
      }
    } else if (sliderSection.nextElementSibling === toneSection) {
      sliderSection.parentElement.insertBefore(toneSection, sliderSection);
    }
  };

  syncCoreStoryOrder();
  matchMedia('(min-width: 990px)').addEventListener('change', syncCoreStoryOrder);

  const syncAccordionState = (button) => {
    const panel = button.nextElementSibling;
    const tab = button.parentElement;
    if (!panel?.classList.contains('collapsible-content') || !tab?.classList.contains('collapsible-tab')) {
      return;
    }

    if (!panel.id) {
      const index = [...document.querySelectorAll('.ciari-pdp-main collapsible-button')].indexOf(button);
      panel.id = `CiariPdpAccordion-${index + 1}`;
    }
    button.setAttribute('role', 'button');
    button.setAttribute('aria-controls', panel.id);
    button.setAttribute('aria-expanded', String(tab.classList.contains('open')));
  };

  const initializeAccordions = () => {
    document.querySelectorAll('.ciari-pdp-main collapsible-button').forEach(syncAccordionState);
  };

  initializeAccordions();
  customElements.whenDefined('collapsible-button').then(initializeAccordions);
  document.addEventListener('shopify:section:load', initializeAccordions);
  window.eventBus?.on('variant:updated', initializeAccordions);

  // A variant without its own image (e.g. Left Handed) borrows the image of the same-colour
  // variant that shares the most options with it, so the pickup choice still changes the photo.
  const getClosestVariantMediaId = (pdp, variantId) => {
    let data;
    try {
      data = JSON.parse(pdp?.querySelector('[data-ciari-variant-media]')?.textContent || 'null');
    } catch (error) {
      return 0;
    }
    if (!data) return 0;

    const selected = data.variants.find((variant) => variant.id === (variantId || data.selected));
    if (!selected) return 0;

    let closestMediaId = 0;
    let closestScore = -1;
    data.variants.forEach((variant) => {
      if (!variant.media) return;
      if (data.colorIndex >= 0 && variant.options[data.colorIndex] !== selected.options[data.colorIndex]) return;
      const score = variant.options.filter((value, index) => value === selected.options[index]).length;
      if (score > closestScore) {
        closestScore = score;
        closestMediaId = variant.media;
      }
    });
    return closestMediaId;
  };

  const syncCoreVariantMediaFallback = (context = {}) => {
    const section = context.sectionId
      ? document.querySelector(`.ciari-pdp-main--core product-information[data-section-id="${CSS.escape(String(context.sectionId))}"]`)
      : document.querySelector('.ciari-pdp-main--core product-information');
    const selectedColor = section
      ?.querySelector('.ciari-pdp-option--finish-color input:checked')
      ?.closest('.swatch-element');
    const pdp = section?.closest('.ciari-pdp-main--core');
    const productMediaElements = pdp?.querySelectorAll('product-media');
    const nativeMediaId = Number(selectedColor?.dataset.mediaId);
    const variantId = Number(context.variant?.id || new URLSearchParams(window.location.search).get('variant'));
    const mediaId =
      getClosestVariantMediaId(pdp, variantId) || Number(selectedColor?.dataset.ciariRepresentativeMediaId);
    if (!section || !productMediaElements) return;

    productMediaElements.forEach((productMedia) => {
      productMedia.changeVariantBasedOnThumbnail = false;
    });

    // A variant media set always opens on its first item, after the theme's featured-media slide
    if (pdp.querySelector('[data-ciari-media-set]:not([data-ciari-media-set="all"])')) {
      setTimeout(() => {
        productMediaElements.forEach((productMedia) => {
          (productMedia.mainMediaSwiper || productMedia.mainMedia?.swiper)?.slideTo(0, 0, false);
        });
      });
      return;
    }
    if (nativeMediaId || !mediaId) return;

    productMediaElements.forEach((productMedia) => {
      productMedia.selectedVariant = {
        ...(context.variant || productMedia.selectedVariant || {}),
        featured_media: { id: mediaId },
      };
      const activeSlideIndex = productMedia.getActiveSlideIndexByMediaId?.(mediaId);
      const slider = productMedia.mainMediaSwiper || productMedia.mainMedia?.swiper;
      if (activeSlideIndex >= 0) slider?.slideTo(activeSlideIndex, 0, false);
    });
  };

  window.eventBus?.on('variant:updated', syncCoreVariantMediaFallback);
  window.eventBus?.on('slider:media:ready', syncCoreVariantMediaFallback);
  customElements.whenDefined('product-media').then(() => syncCoreVariantMediaFallback());

  const syncColorMediaGalleries = (context = {}) => {
    const productInformation = context.sectionId
      ? document.querySelector(`.ciari-pdp-main product-information[data-section-id="${CSS.escape(String(context.sectionId))}"]`)
      : document.querySelector('.ciari-pdp-main product-information');
    const section = productInformation?.closest('.ciari-pdp-main');
    const selectedColor = productInformation
      ?.querySelector('.ciari-pdp-option--finish-color input:checked')
      ?.closest('.swatch-element');
    const mediaId = Number(selectedColor?.dataset.mediaId || selectedColor?.dataset.ciariRepresentativeMediaId);
    const slides = [...(section?.querySelectorAll(
      '.product-media-slider-desktop [data-media-main] > .swiper-wrapper > [data-media-id]',
    ) || [])];
    const matchedBaseIndex = slides.findIndex((slide) => Number(slide.dataset.mediaId) === mediaId);
    const hasMediaSet = section?.querySelector('[data-ciari-media-set]:not([data-ciari-media-set="all"])');
    const baseIndex = !hasMediaSet && matchedBaseIndex >= 0 ? matchedBaseIndex : 0;
    if (!section) return;

    section.querySelectorAll('[data-ciari-color-media-gallery]').forEach((gallery) => {
      let visibleItems = 0;
      gallery.querySelectorAll('[data-ciari-pdp-media-offset]').forEach((item) => {
        const mediaOffset = Number(item.dataset.ciariPdpMediaOffset);
        let mediaIndex = baseIndex + mediaOffset;
        let sourceImage = slides[mediaIndex]?.querySelector('img');
        if (!sourceImage && mediaOffset === 7) {
          const fallbackOffset = [6, 5].find((offset) => slides[baseIndex + offset]?.querySelector('img'));
          if (fallbackOffset !== undefined) {
            mediaIndex = baseIndex + fallbackOffset;
            sourceImage = slides[mediaIndex]?.querySelector('img');
          }
        }
        const targetImage = item.matches('img') ? item : item.querySelector('img');
        item.hidden = !sourceImage || !targetImage;
        if (!sourceImage || !targetImage) return;

        const sourceUrl = new URL(sourceImage.currentSrc || sourceImage.src, window.location.href);
        sourceUrl.searchParams.set('width', '720');
        targetImage.removeAttribute('srcset');
        targetImage.src = sourceUrl.toString();
        targetImage.alt = sourceImage.alt || document.querySelector('.ciari-pdp-main h1')?.textContent.trim() || '';
        if (!item.matches('img')) {
          item.dataset.ciariPdpMediaIndex = String(mediaIndex);
          item.setAttribute('aria-label', `Show product media ${mediaIndex + 1}`);
        }
        visibleItems += 1;
      });
      gallery.hidden = visibleItems === 0;
    });
  };

  window.eventBus?.on('variant:updated', syncColorMediaGalleries);
  window.eventBus?.on('slider:media:ready', syncColorMediaGalleries);
  customElements.whenDefined('product-media').then(() => syncColorMediaGalleries());

  const syncMediaThumbnailSelection = (section, slider) => {
    if (!section || !slider) return;
    const activeIndex = Number(slider.realIndex ?? slider.activeIndex);
    if (!Number.isFinite(activeIndex)) return;
    section.querySelectorAll('[data-ciari-pdp-media-index]').forEach((thumbnail) => {
      thumbnail.setAttribute(
        'aria-current',
        String(Number(thumbnail.dataset.ciariPdpMediaIndex) === activeIndex),
      );
    });
  };

  const scrollMediaSliderIntoView = (slider) => {
    if (!slider) return;
    const { top, bottom } = slider.getBoundingClientRect();
    if (bottom > 0 && top < window.innerHeight) return;

    slider.scrollIntoView({
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });
  };

  const initializeMediaThumbnailSelections = (context = {}) => {
    const sections = context.sectionId
      ? [document.querySelector(`.ciari-pdp-main product-information[data-section-id="${CSS.escape(String(context.sectionId))}"]`)?.closest('.ciari-pdp-main')]
      : [...document.querySelectorAll('.ciari-pdp-main')];

    sections.filter(Boolean).forEach((section) => {
      section.querySelectorAll('product-media').forEach((productMedia) => {
        const slider = productMedia.mainMediaSwiper || productMedia.mainMedia?.swiper;
        if (!slider) return;
        if (!slider.ciariThumbnailSelectionBound) {
          slider.ciariThumbnailSelectionBound = true;
          slider.on('slideChange', () => {
            const activeDevice = matchMedia('(min-width: 990px)').matches ? 'desktop' : 'mobile';
            if (productMedia.dataset.device === activeDevice) {
              syncMediaThumbnailSelection(section, slider);
            }
          });
        }

        const activeDevice = matchMedia('(min-width: 990px)').matches ? 'desktop' : 'mobile';
        if (productMedia.dataset.device === activeDevice) {
          syncMediaThumbnailSelection(section, slider);
        }
      });
    });
  };

  window.eventBus?.on('variant:updated', initializeMediaThumbnailSelections);
  window.eventBus?.on('slider:media:ready', (context) => {
    requestAnimationFrame(() => initializeMediaThumbnailSelections(context));
  });
  customElements.whenDefined('product-media').then(() => initializeMediaThumbnailSelections());
  window.addEventListener('load', () => initializeMediaThumbnailSelections());
  window.addEventListener('resize', () => initializeMediaThumbnailSelections());
  document.addEventListener('shopify:section:load', () => initializeMediaThumbnailSelections());

  const syncCoreFinishColorPosition = (context = {}) => {
    const sections = context.sectionId
      ? [document.querySelector(`.ciari-pdp-main--core product-information[data-section-id="${CSS.escape(String(context.sectionId))}"]`)?.closest('.ciari-pdp-main--core')]
      : [...document.querySelectorAll('.ciari-pdp-main--core')];

    sections.filter(Boolean).forEach((section) => {
      const finishColor = section.querySelector('.ciari-pdp-option--finish-color');
      if (!finishColor) return;
      if (!matchMedia('(min-width: 990px)').matches) {
        finishColor.style.removeProperty('--ciari-finish-color-top');
        return;
      }

      const productMedia = section.querySelector('product-media.product-media-slider-desktop');
      const activeImage = productMedia?.querySelector('[data-media-main] .swiper-slide-active img');
      const thumbnailStrip = productMedia?.querySelector('[data-media-thumbnails]');
      const offsetParent = finishColor.offsetParent;
      if (!activeImage || !offsetParent) return;

      const updatePosition = () => {
        if (!activeImage.isConnected || !finishColor.isConnected) return;
        const imageRect = activeImage.getBoundingClientRect();
        const thumbnailRect = thumbnailStrip?.getBoundingClientRect();
        const offsetParentRect = offsetParent.getBoundingClientRect();
        const mediaBottom = thumbnailRect?.height ? thumbnailRect.bottom : imageRect.bottom;
        const top = Math.round(mediaBottom - offsetParentRect.top + 20);
        finishColor.style.setProperty('--ciari-finish-color-top', `${top}px`);
      };

      if (!activeImage.complete) activeImage.addEventListener('load', updatePosition, { once: true });
      requestAnimationFrame(updatePosition);
    });
  };

  const initializeCoreFinishColorPosition = (context = {}) => {
    syncCoreFinishColorPosition(context);
    document.querySelectorAll('.ciari-pdp-main--core product-media.product-media-slider-desktop').forEach((productMedia) => {
      const slider = productMedia.mainMediaSwiper || productMedia.mainMedia?.swiper;
      if (!slider || slider.ciariFinishColorPositionBound) return;
      slider.ciariFinishColorPositionBound = true;
      slider.on('transitionEnd', () => syncCoreFinishColorPosition());
    });
  };

  window.eventBus?.on('variant:updated', initializeCoreFinishColorPosition);
  window.eventBus?.on('slider:media:ready', initializeCoreFinishColorPosition);
  customElements.whenDefined('product-media').then(() => initializeCoreFinishColorPosition());
  window.addEventListener('resize', () => syncCoreFinishColorPosition());
  document.addEventListener('shopify:section:load', () => initializeCoreFinishColorPosition());

  const syncVariantFormImmediately = (input) => {
    if (!input?.matches('[data-product-variant]')) return;
    const section = input.closest('.ciari-pdp-main');
    const swatch = input.closest('product-swatch');
    const variantData = swatch?.querySelector(`script[type="application/json"][data-resource="${input.id}"]`);
    if (!section || !variantData) return;

    let variant;
    try {
      variant = JSON.parse(variantData.textContent);
    } catch (_error) {
      return;
    }
    if (!variant?.id) return;

    section.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((variantInput) => {
      variantInput.value = String(variant.id);
    });
    section.querySelectorAll('form[action*="/cart/add"] [name="add"]').forEach((button) => {
      button.toggleAttribute('disabled', !variant.available);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const reviewButton = event.target.closest('.kl_reviews__button');
      if (reviewButton?.textContent.trim().toLowerCase() === 'show more') {
        const reviewRoot = reviewButton.closest('.ciari-pdp-reviews');
        reviewRoot?.classList.add('ciari-reviews-expanded');
        reviewRoot?.closest('.shopify-section')?.classList.add('ciari-reviews-expanded');
      }
    },
    true,
  );

  document.addEventListener('click', (event) => {
    const editorialAddToCart = event.target.closest('[data-ciari-product-atc]');
    if (editorialAddToCart) {
      const productButton = document.querySelector('.ciari-pdp-main form[action*="/cart/add"] [name="add"]');
      if (productButton && !productButton.disabled) {
        event.preventDefault();
        productButton.click();
      }
      return;
    }

    const pickupGuide = event.target.closest('[data-ciari-pickup-guide]');
    if (pickupGuide) {
      const panel = document.getElementById(pickupGuide.getAttribute('aria-controls'));
      const expanded = pickupGuide.getAttribute('aria-expanded') === 'true';
      pickupGuide.setAttribute('aria-expanded', String(!expanded));
      if (panel) panel.hidden = expanded;
      return;
    }

    const accordionButton = event.target.closest('.ciari-pdp-main collapsible-button');
    if (accordionButton) queueMicrotask(() => syncAccordionState(accordionButton));

    const specTab = event.target.closest('[data-ciari-spec-tab]');
    if (specTab) {
      const specs = specTab.closest('.cpde__specs');
      const group = specTab.dataset.ciariSpecTab;
      specs.querySelectorAll('[data-ciari-spec-tab]').forEach((tab) => {
        const selected = tab === specTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      specs.querySelectorAll('[data-ciari-spec-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.ciariSpecPanel !== group;
      });
      specs.querySelectorAll('[data-ciari-spec-media]').forEach((panel) => {
        panel.hidden = panel.dataset.ciariSpecMedia !== group;
      });
      specTab.focus();
      return;
    }
    if (!event.isTrusted) return;
    const mediaThumbnail = event.target.closest('[data-ciari-pdp-media-index]');
    if (mediaThumbnail) {
      const section = mediaThumbnail.closest('.ciari-pdp-main');
      const sliderSelector = matchMedia('(min-width: 990px)').matches
        ? '.product-media-slider-desktop .thumbnail-media--main-media'
        : '.product-media-slider-mobile .product-media-slider-main';
      const sliderElement = section?.querySelector(sliderSelector);
      const slider = sliderElement?.swiper;
      const mediaIndex = Number(mediaThumbnail.dataset.ciariPdpMediaIndex);
      if (typeof slider?.slideToLoop === 'function') slider.slideToLoop(mediaIndex);
      else slider?.slideTo(mediaIndex);
      syncMediaThumbnailSelection(section, slider);
      scrollMediaSliderIntoView(sliderElement);
      return;
    }
    const mediaControl = event.target.closest('[data-ciari-pdp-media-prev], [data-ciari-pdp-media-next]');
    if (mediaControl) {
      const section = mediaControl.closest('.ciari-pdp-main');
      const sliderSelector = matchMedia('(min-width: 990px)').matches
        ? '.product-media-slider-desktop .thumbnail-media--main-media'
        : '.product-media-slider-mobile .product-media-slider-main';
      const activeSlider = section?.querySelector(sliderSelector);
      if (mediaControl.hasAttribute('data-ciari-pdp-media-prev')) activeSlider?.swiper?.slidePrev();
      else activeSlider?.swiper?.slideNext();
      return;
    }
  });

  const syncSignatureTrustRating = (trust) => {
    const appBlock = trust.closest('.product-form-block--type-app');
    const ratingLabel = appBlock?.querySelector('[aria-label*="star" i]')?.getAttribute('aria-label') || '';
    const rating = ratingLabel.match(/([0-9]+(?:\.[0-9]+)?)\s*stars?/i)?.[1];
    const reviewCountMatch = ratingLabel.match(/([0-9,]+)\s*reviews?/i);
    const reviewCount = Number(reviewCountMatch?.[1]?.replaceAll(',', ''));
    const ratingValue = trust.querySelector('[data-ciari-signature-rating]');
    const numericRating = Number(rating);
    const hasReviews = Number.isFinite(numericRating)
      && numericRating > 0
      && (!reviewCountMatch || reviewCount > 0);
    if (ratingValue) {
      const nextRating = hasReviews ? `${numericRating.toFixed(1)}/5` : '';
      const shouldHide = !nextRating;
      if (ratingValue.textContent !== nextRating) ratingValue.textContent = nextRating;
      if (ratingValue.hidden !== shouldHide) ratingValue.hidden = shouldHide;
    }
    if (appBlock && appBlock.hidden === hasReviews) appBlock.hidden = !hasReviews;
    return hasReviews;
  };

  const syncSignatureTrustRatings = () => {
    document.querySelectorAll('[data-ciari-signature-trust]').forEach((trust) => {
      const appBlock = trust.closest('.product-form-block--type-app');
      syncSignatureTrustRating(trust);
      if (!appBlock || trust.ciariRatingObserver) return;

      // Klaviyo hydrates its rating markup after the theme script on some page loads.
      trust.ciariRatingObserver = new MutationObserver((mutations) => {
        if (mutations.every((mutation) => trust.contains(mutation.target))) return;
        syncSignatureTrustRating(trust);
      });
      trust.ciariRatingObserver.observe(appBlock, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label'] });
    });
  };

  syncSignatureTrustRatings();
  window.eventBus?.on('variant:updated', syncSignatureTrustRatings);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ciari-signature-trust]').forEach((trust) => {
      trust.ciariRatingObserver?.disconnect();
      trust.ciariRatingObserver = null;
    });
  });

  document.addEventListener('change', (event) => {
    if (!event.isTrusted) return;
    syncVariantFormImmediately(event.target);
  });

  document.addEventListener('keydown', (event) => {
    const accordionButton = event.target.closest('.ciari-pdp-main collapsible-button');
    if (accordionButton && event.key === ' ') {
      event.preventDefault();
      accordionButton.click();
      return;
    }
    if (accordionButton && event.key === 'Enter') {
      queueMicrotask(() => syncAccordionState(accordionButton));
    }

    const currentTab = event.target.closest('[data-ciari-spec-tab]');
    if (!currentTab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const tabs = [...currentTab.closest('[role="tablist"]').querySelectorAll('[data-ciari-spec-tab]')];
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    tabs[(tabs.indexOf(currentTab) + direction + tabs.length) % tabs.length].click();
  });
})();
